import React, { Component } from 'react';
import classnames from 'classnames';
import { CircularProgress } from '@mui/material';
import { map, filter, isEqual, omit } from 'lodash';
import { NOTIFICATION_EVENT_TYPE_MONITOR } from '../../../constants/resourceTypes';

import { SOURCE } from '../../../constants/notificationsTypes';
import { APP, EMAIL } from '../../../constants/notificationChannels';
import UIUtils from '../../../lib/UIUtils';

import TimeSelector from '../TimeSelector';
import { Button } from 'new/elements/Button';
import {
  SelectInput,
  SelectOption,
  baseSelectStyles,
  getCurrentSelectedOption,
  getNewSelectionValue,
} from '../rselect/ReactSelectUtils';
import './NotificationListView.css';
import SortedSelect from '../rselect/SortedSelect';
import UnsortedSelect from '../rselect/UnsortedSelect';
import { Stack } from 'new/elements/Stack';
import { ReactComponent as BellRingFilled } from 'new/components/icons/bellRingFilled.svg';
import { getKeyByValue } from 'new/utils';

const INTERVAL_OPTIONS = [
  {
    label: 'Daily',
    value: 'Day',
  },
  {
    label: 'Hourly',
    value: 'Hour',
  },
  {
    label: 'Continuously',
    isDisabled: true,
  },
];
const MONITOR_WINDOW_OPTIONS = [
  {
    label: '1 hour',
    value: 3600000,
  },
  {
    label: '2 hours',
    value: 7200000,
  },
  {
    label: '4 hours',
    value: 14400000,
  },
  {
    label: '8 hours',
    value: 28800000,
  },
  {
    label: '24 hours',
    value: 86400000,
  },
];
const THRESHOLD_OPTIONS = [
  {
    label: '10%',
    value: '10%',
  },
  {
    label: '30%',
    value: '30%',
  },
  {
    label: '90%',
    value: '90%',
  },
  {
    label: '2-sigma',
    value: '2-sigma',
  },
  {
    label: '1-sigma',
    value: '1-sigma',
  },
];
const THRESHOLD_CONDITION_OPTIONS = [
  {
    label: 'increase',
    value: 'greaterThan',
  },
  {
    label: 'decrease',
    value: 'lessThan',
  },
  {
    label: 'change',
    value: 'change',
  },
];

const thresholdComparisonWindowMappedValue = Object.freeze({
  yesterday: 1,
  'same day last week': 7,
  'daily rolling average over 14 days': 14,
});

const PERCENT_THRESHOLD_COMPARISON_WINDOW_OPTIONS = [
  {
    label: 'yesterday',
    value: 'yesterday',
  },
];
const SIGMA_THRESHOLD_COMPARISON_WINDOW_OPTIONS = [
  {
    label: 'same day last week',
    value: 'same day last week',
  },
  {
    label: 'daily rolling average over 14 days',
    value: 'daily rolling average over 14 days',
  },
];

const styles = {
  buttonCTA: {},
  buttonCTAText: {
    fontSize: 11,
  },
};

const selectStyles = {
  ...baseSelectStyles,
  control: (styles) => ({
    ...styles,
    border: 'none',
    borderBottom: '2px solid var(--color-primary-500)',
    borderRadius: 0,
    color: 'var(--color-primary-500)',
  }),
  input: (styles) => ({
    ...styles,
    color: 'var(--color-primary-500)',
    fontSize: 12,
  }),
  valueContainer: (styles) => ({
    ...styles,
    fontSize: 12,
  }),
  singleValue: (styles) => ({
    ...styles,
    color: 'var(--color-primary-500)',
  }),
  dropdownIndicator: (styles) => ({
    ...styles,
    color: 'var(--color-greyscale-600)',
    padding: '4px 0',
  }),
  container: (base) => ({ ...base, minWidth: 150 }),
};

class SourceNotificationSettings extends Component {
  constructor(props) {
    super(props);
    this.state = {
      expandedId: null,
      valuesByRow: [],
    };

    this._renderSettingsRow = this._renderSettingsRow.bind(this);
    this._renderSelectField = this._renderSelectField.bind(this);
    this._handleCreateNewAlertClicked = this._handleCreateNewAlertClicked.bind(this);
    this._handleSaveClicked = this._handleSaveClicked.bind(this);
    this._handleDeleteClicked = this._handleDeleteClicked.bind(this);
    this._handleValueChanged = this._handleValueChanged.bind(this);
    this._updateStateFromProps = this._updateStateFromProps.bind(this);
    this._updateNotificationTypesFromProps = this._updateNotificationTypesFromProps.bind(this);
    this._updateNotificationChannelsFromProps =
      this._updateNotificationChannelsFromProps.bind(this);
    this._fetchNotificationSettings = this._fetchNotificationSettings.bind(this);
  }

  componentDidMount() {
    if (this.props.sortedIds && this.props.sortedIds.length > 0) {
      this._updateStateFromProps(this.props);
    }

    if (this.props.notificationTypes) {
      this._updateNotificationTypesFromProps(this.props);
    }

    if (this.props.notificationChannelsEnumMap) {
      this._updateNotificationChannelsFromProps(this.props);
    }

    if (this.props.id) {
      this._fetchNotificationSettings(this.props);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      !isEqual(prevProps.sortedIds, this.props.sortedIds) ||
      (prevProps.isLoadingSettings === true && this.props.isLoadingSettings === false) ||
      (prevProps.isSavingSettings === true && this.props.isSavingSettings === false)
    ) {
      this._updateStateFromProps(this.props);
    }

    if (prevProps.notificationTypes !== this.props.notificationTypes) {
      this._updateNotificationTypesFromProps(this.props);
    }

    if (prevProps.notificationChannelsEnumMap !== this.props.notificationChannelsEnumMap) {
      this._updateNotificationChannelsFromProps(this.props);
    }

    if (prevProps.id !== this.props.id) {
      this._fetchNotificationSettings(this.props);
    }
  }

  render() {
    const { isLoadingSettings, isSavingSettings, description } = this.props;
    const { valuesByRow } = this.state;

    const listItems =
      isLoadingSettings || isSavingSettings ? (
        <div
          style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress color="primary" size={32} />
        </div>
      ) : (
        <div className="notification-settings-list">
          {map(valuesByRow, this._renderSettingsRow)}
        </div>
      );

    return (
      <Stack>
        <Stack
          alignItems="center"
          direction="row"
          padding="24px"
          gap="32px"
          className="notification-settings-top"
          justifyContent="space-between"
        >
          <Button
            startIcon={<BellRingFilled />}
            variant="outlined"
            color="error"
            disableElevation
            onClick={this._handleCreateNewAlertClicked}
          >
            <span style={styles.buttonCTAText}>Create New Alert</span>
          </Button>
          <Stack flex="1">{description}</Stack>
        </Stack>
        {listItems}
      </Stack>
    );
  }

  setExpandedId = (id, rowHeight) => {
    this.setState(
      {
        expandedId: id,
        rowHeight: id ? rowHeight : null,
      },
      () => {
        if (this.innerList) {
          this.innerList.recomputeRowHeights();
        }
      },
    );
  };

  _renderSettingsRow(values, index) {
    const { notificationTypes } = this.props;
    const selectedNotificationTypeId =
      values.notificationTypeId ||
      (this._notificationTypeOptions && this._notificationTypeOptions[0].value);
    const selectedNotificationType =
      notificationTypes &&
      this._getNotificationTypeName(notificationTypes[selectedNotificationTypeId]);
    const isNewSetting = !values.id;

    return (
      <li key={index} className={classnames({ 'is-new-setting': isNewSetting })}>
        <div className="setting-item">
          <div className="input-row">
            {this._renderSelectField(index, 'interval', INTERVAL_OPTIONS)}
            {values.interval === 'Day' && (
              <>
                <span className="sentence-text">at</span>
                <TimeSelector
                  selectStyles={selectStyles}
                  interval={values.interval}
                  hh={values.hh}
                  aa={values.aa}
                  showMinutes={false}
                  onChange={(name, value) => this._handleValueChanged(index, name, value)}
                />
              </>
            )}
            <span className="sentence-text">check for</span>
            {this._renderSelectField(
              index,
              'notificationTypeId',
              this._notificationTypeOptions,
              true,
            )}
            <span className="sentence-text">over the past</span>
            {this._renderSelectField(index, 'monitorWindow', MONITOR_WINDOW_OPTIONS)}
            <span className="sentence-text">.</span>
            {this._renderSelectField(index, 'channel', this._notificationChannelOptions, true)}
            <span className="sentence-text">when {selectedNotificationType} indicates</span>
            {this._renderSelectField(index, 'threshold', THRESHOLD_OPTIONS)}
            <span className="sentence-text" />
            {this._renderSelectField(index, 'thresholdCondition', THRESHOLD_CONDITION_OPTIONS)}
            <span className="sentence-text">compared to the same period</span>
            {this._renderSelectField(
              index,
              'thresholdComparisonWindow',
              this._getThresholdComparisonWindowOptions(values.threshold),
              true,
            )}
            .
          </div>
          <div className="button-bar">
            <Button
              variant="text"
              color="error"
              disableElevation
              style={styles.buttonCTA}
              onClick={(event) => {
                event.stopPropagation();
                this._handleDeleteClicked(index);
              }}
            >
              <span style={styles.buttonCTAText}>{isNewSetting ? 'Cancel' : 'Delete'}</span>
            </Button>
            &nbsp;
            <Button
              variant="text"
              color="primary"
              disableElevation
              style={styles.buttonCTA}
              onClick={(event) => {
                event.stopPropagation();
                this._handleSaveClicked(index);
              }}
            >
              <span style={styles.buttonCTAText}>Save</span>
            </Button>
          </div>
        </div>
      </li>
    );
  }

  _renderSelectField(rowIndex, name, options, sort = false) {
    const { valuesByRow } = this.state;
    const selectionValue = getCurrentSelectedOption(options, valuesByRow[rowIndex][name]);

    return sort === true ? (
      <SortedSelect
        styles={selectStyles}
        value={selectionValue}
        options={options}
        components={{ Input: SelectInput, Option: SelectOption }}
        isClearable={false}
        backspaceRemovesValue={false}
        isSearchable={true}
        onChange={(value) => this._handleValueChanged(rowIndex, name, value)}
      />
    ) : (
      <UnsortedSelect
        styles={selectStyles}
        value={selectionValue}
        options={options}
        components={{ Input: SelectInput, Option: SelectOption }}
        isClearable={false}
        backspaceRemovesValue={false}
        isSearchable={true}
        onChange={(value) => this._handleValueChanged(rowIndex, name, value)}
      />
    );
  }

  _handleCreateNewAlertClicked() {
    const { valuesByRow } = this.state;
    const newRowValues = this._getDefaultRowValues();
    const newValuesByRow = [newRowValues, ...valuesByRow];

    this.setState({ valuesByRow: newValuesByRow });
  }

  _handleSaveClicked(index) {
    const { id: resourceId, updateNotificationManager } = this.props;
    const { valuesByRow } = this.state;
    const rowValues = valuesByRow[index];
    const {
      id,
      notificationTypeId,
      channel,
      interval,
      hh,
      aa,
      monitorWindow,
      threshold,
      thresholdCondition,
      thresholdComparisonWindow,
    } = rowValues;
    const frequencyCron = UIUtils.getCron({ interval, hh, aa });
    const config = {
      frequency_cron: frequencyCron,
      monitor_window: monitorWindow,
      threshold_value: parseInt(threshold, 10),
      threshold_type: threshold.endsWith('%') ? 'PERCENTAGE' : 'SIGMA',
      threshold_condition: thresholdCondition,
      threshold_comparison_window: thresholdComparisonWindowMappedValue[thresholdComparisonWindow],
    };

    updateNotificationManager(
      SOURCE,
      resourceId,
      notificationTypeId,
      channel,
      true,
      id,
      config,
    ).then(() => this._fetchNotificationSettings(this.props));
  }

  _handleDeleteClicked(index) {
    const { deleteResourceNotificationSetting, id: resourceId } = this.props;
    const { valuesByRow } = this.state;
    const rowValues = valuesByRow[index];
    const { id } = rowValues;

    if (id) {
      deleteResourceNotificationSetting(id, SOURCE, resourceId);
      // .then(() => this._fetchNotificationSettings(this.props))
      // .catch(() => this._fetchNotificationSettings(this.props));
    }

    const newValuesByRow = [...valuesByRow];
    newValuesByRow.splice(index, 1);

    this.setState({ valuesByRow: newValuesByRow });
  }

  _handleValueChanged(rowIndex, name, valueObj) {
    const { valuesByRow } = this.state;
    const newValuesByRow = [...valuesByRow];
    let rowValues = valuesByRow[rowIndex];

    let value = getNewSelectionValue(valueObj, true);

    switch (name) {
      case 'interval':
        if (valueObj.value === 'Hour') {
          rowValues = omit(rowValues, ['hh', 'aa']);
        }
        break;
      case 'threshold':
        const thresholdComparisonWindowOptions = this._getThresholdComparisonWindowOptions(value);
        rowValues = {
          ...rowValues,
          thresholdComparisonWindow: thresholdComparisonWindowOptions[0].value,
        };
        break;
      default:
    }

    newValuesByRow[rowIndex] = {
      ...rowValues,
      [name]: value,
    };

    this.setState({ valuesByRow: newValuesByRow });
  }

  _updateStateFromProps(props) {
    const { notificationSettings } = props;
    const valuesByRow = map(notificationSettings, (setting) => {
      const {
        id,
        channel,
        notification_type_id,
        config: {
          frequency_cron,
          monitor_window,
          threshold_value,
          threshold_type,
          threshold_condition,
          threshold_comparison_window,
        },
      } = setting;

      const { interval, hh, aa } = UIUtils.parseCron(frequency_cron);
      const threshold = threshold_value + (threshold_type === 'PERCENTAGE' ? '%' : '-sigma');
      const defaultValues = this._getDefaultRowValues(threshold);

      let rowValues = {
        ...defaultValues,
        id,
        interval,
        hh,
        aa,
        notificationTypeId: notification_type_id,
        monitorWindow: monitor_window,
        channel,
        threshold,
        thresholdCondition: threshold_condition,
        thresholdComparisonWindow:
          getKeyByValue(thresholdComparisonWindowMappedValue, threshold_comparison_window) ??
          'yesterday',
      };

      if (interval === 'Hour') {
        rowValues = omit(rowValues, ['hh', 'aa']);
      }

      return rowValues;
    });

    this.setState({ valuesByRow });
  }

  _updateNotificationTypesFromProps(props) {
    this._notificationTypeOptions = map(
      filter(
        props.notificationTypes,
        (notificationType) =>
          notificationType.resource_type === SOURCE &&
          notificationType.event_type === NOTIFICATION_EVENT_TYPE_MONITOR,
      ),
      (notificationType) => ({
        label: this._getNotificationTypeName(notificationType),
        value: notificationType.id,
      }),
    );
  }

  _updateNotificationChannelsFromProps(props) {
    this._notificationChannelOptions = map(
      props.notificationChannelsEnumMap,
      (channelId, channelKey) => {
        let channelName;
        let isDisabled = false;

        switch (channelKey) {
          case APP:
            channelName = 'Notify in-app';
            break;
          case EMAIL:
            channelName = 'E-mail';
            break;
          default:
            channelName = channelKey;
            isDisabled = true;
        }

        return {
          label: channelName,
          value: channelKey,
          isDisabled,
        };
      },
    );
  }

  _fetchNotificationSettings(props) {
    const {
      id,
      notificationChannelsEnumMap,
      notificationTypes,
      loadNotificationsTypes,
      loadNotificationsChannels,
      loadNotificationsSettings,
    } = props;

    if (!notificationTypes) {
      loadNotificationsTypes();
    }

    if (!notificationChannelsEnumMap) {
      loadNotificationsChannels();
    }

    loadNotificationsSettings(SOURCE, id);
  }

  _getDefaultRowValues(threshold = null) {
    const effectiveThreshold = threshold || THRESHOLD_OPTIONS[0].value;
    const thresholdComparisonWindowOptions =
      this._getThresholdComparisonWindowOptions(effectiveThreshold);

    return {
      id: 0,
      interval: INTERVAL_OPTIONS[0].value,
      hh: '12',
      aa: 'PM',
      notificationTypeId: this._notificationTypeOptions && this._notificationTypeOptions[0].value,
      monitorWindow: MONITOR_WINDOW_OPTIONS[0].value,
      channel: this._notificationChannelOptions && this._notificationChannelOptions[0].value,
      threshold: effectiveThreshold,
      thresholdCondition: THRESHOLD_CONDITION_OPTIONS[0].value,
      thresholdComparisonWindow: thresholdComparisonWindowOptions[0].value,
    };
  }

  _getThresholdComparisonWindowOptions(threshold) {
    return threshold.endsWith('%')
      ? PERCENT_THRESHOLD_COMPARISON_WINDOW_OPTIONS
      : SIGMA_THRESHOLD_COMPARISON_WINDOW_OPTIONS;
  }

  _getNotificationTypeName(notificationType) {
    switch (notificationType.id) {
      case 9:
        return 'record count';
      default:
        return notificationType.name;
    }
  }
}

export default SourceNotificationSettings;
